import { useElementVisibility } from "@judo/core";
import { useModelLabel } from "@judo/i18n";
import type { Flex, VisualElement } from "@judo/model-api";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { mapCrossAxisAlignment, mapMainAxisAlignment } from "../utils/alignment-mappers";
import { DEFAULT_COL, type GridSize, calculateChildGridSize, isVerticalLayout } from "../utils/flex-layout";
import { FrameRenderer } from "./FrameRenderer";
import { VisualElementRenderer } from "./VisualElementRenderer";
import "../styles.css";

export interface FlexRendererProps {
	element: Flex;
	readOnly?: boolean;
}

/**
 * Renderer for Flex layout containers.
 * - Horizontal layouts: Use MUI Grid2 for responsive 12-column grid layout
 * - Vertical layouts: Use MUI Stack with force-full-width class
 * Each child's `col` property (1-12) is scaled based on parent's column span.
 */
export function FlexRenderer({ element, readOnly }: FlexRendererProps) {
	const isVertical = isVerticalLayout(element.direction);
	const justifyContent = mapMainAxisAlignment(element.mainAxisAlignment);
	const alignItems = mapCrossAxisAlignment(element.crossAxisAlignment);

	// Get parent's column span for size calculation
	const parentCol = element.col ?? DEFAULT_COL;

	// Common size constraints
	const sizeStyles = {
		width: element.size?.width ?? "100%",
		height: element.size?.height,
		minWidth: element.sizeConstraint?.minWidth,
		minHeight: element.sizeConstraint?.minHeight,
		maxWidth: element.sizeConstraint?.maxWidth ?? "100%",
		maxHeight: element.sizeConstraint?.maxHeight,
	};

	// Render children with Grid wrappers (using FlexChild to handle hiddenBy without Grid gaps)
	const children = element.children?.map((child) => {
		const childCol = child.col ?? DEFAULT_COL;
		const gridSize = calculateChildGridSize(element.direction, parentCol, childCol);

		return <FlexChild key={child["xmi:id"]} child={child} gridSize={gridSize} readOnly={readOnly} />;
	});

	const content = isVertical ? (
		<Stack
			spacing={2}
			className="force-full-width"
			data-testid={`flex::${element.sourceId ?? element["xmi:id"]}`}
			sx={sizeStyles}
		>
			{children}
		</Stack>
	) : (
		<Grid
			container
			justifyContent={justifyContent}
			alignItems={alignItems}
			spacing={2}
			data-testid={`flex::${element.sourceId ?? element["xmi:id"]}`}
			sx={sizeStyles}
		>
			{children}
		</Grid>
	);

	// Wrap in frame if specified, passing label and icon for header
	if (element.frame) {
		return <TranslatedFrame element={element}>{content}</TranslatedFrame>;
	}

	return content;
}

/** Frame wrapper that translates the label via useModelLabel. */
function TranslatedFrame({ element, children }: { element: Flex; children: React.ReactNode }) {
	const { label } = useModelLabel(element);
	return (
		<FrameRenderer frame={element.frame!} label={label} icon={element.icon}>
			{children}
		</FrameRenderer>
	);
}

/**
 * Wrapper component for Flex children that handles visibility.
 * When a child element is hidden (via hiddenBy), both the Grid wrapper
 * and the element are removed from the layout to avoid empty grid gaps.
 */
function FlexChild({ child, gridSize, readOnly }: { child: VisualElement; gridSize: GridSize; readOnly?: boolean }) {
	const hidden = useElementVisibility(child);
	if (hidden) {
		return null;
	}

	return (
		<Grid size={gridSize} sx={{ minWidth: 0 }}>
			<VisualElementRenderer element={child} readOnly={readOnly} />
		</Grid>
	);
}
